import { getAdminContext, isRecord, unwrapResponseJson, wpFetch } from '../../data/wpApi'
import type { DeckPlayerBootstrapV1, DeckPlayerModel } from '../types'

const NAMESPACE = '/perlingo-flashcards/v1'

const normalizeBase = (value: string) => value.replace(/\/+$/, '')

const inferRestRootFromLink = (): string | null => {
  try {
    const link = document.querySelector('link[rel="https://api.w.org/"]') as HTMLLinkElement | null
    const href = link?.href
    if (!href) return null
    return normalizeBase(href)
  } catch {
    return null
  }
}

const inferRestRoot = (): { restRoot: string; nonce?: string } | null => {
  // 1) WP admin context (wp-admin)
  const ctx = typeof window !== 'undefined' ? getAdminContext() : null
  if (ctx?.restUrl) {
    const base = normalizeBase(ctx.restUrl)
    // ctx.restUrl might already be namespaced.
    if (base.endsWith(NAMESPACE)) {
      return { restRoot: base.slice(0, -NAMESPACE.length), nonce: ctx.nonce }
    }
    if (base.endsWith('/wp-json')) {
      return { restRoot: base, nonce: ctx.nonce }
    }
    // Best effort.
    return { restRoot: base, nonce: ctx.nonce }
  }

  // 2) wpApiSettings (common on frontend when WP API script is enqueued)
  const win = window as unknown as { wpApiSettings?: unknown }
  const raw = win.wpApiSettings
  if (isRecord(raw)) {
    const root = typeof raw.root === 'string' ? raw.root : ''
    const nonce = typeof raw.nonce === 'string' ? raw.nonce : undefined
    if (root) {
      // `root` is usually .../wp-json/
      return { restRoot: normalizeBase(root), nonce }
    }
  }

  // 3) REST discovery link tag
  const discovered = inferRestRootFromLink()
  if (discovered) return { restRoot: discovered }

  return null
}

const joinApi = (restRoot: string, path: string) => {
  const base = normalizeBase(restRoot)
  const normalizedPath = path.startsWith('/') ? path : `/${path}`

  const alreadyNamespaced = base.endsWith(NAMESPACE) || base.includes(`${NAMESPACE}/`)
  if (alreadyNamespaced) return `${base}${normalizedPath}`
  return `${base}${NAMESPACE}${normalizedPath}`
}

export const resolvePerlingoRestContext = (opts?: {
  restRoot?: string
  nonce?: string
}): { restRoot: string; nonce?: string } => {
  const inferred = !opts?.restRoot ? inferRestRoot() : null
  const restRoot = opts?.restRoot || inferred?.restRoot
  const nonce = opts?.nonce || inferred?.nonce

  if (!restRoot) {
    throw new Error(
      'Deckplayer: missing REST root. Provide restRoot or ensure wpApiSettings/root or <link rel="https://api.w.org/"> exists.',
    )
  }

  return { restRoot, nonce }
}

export const joinPerlingoApi = (restRoot: string, path: string) => joinApi(restRoot, path)

const isBootstrap = (json: unknown): json is DeckPlayerBootstrapV1 => {
  const unwrapped = unwrapResponseJson(json)
  if (!isRecord(unwrapped)) return false
  return (
    typeof unwrapped.version === 'number' &&
    typeof unwrapped.model === 'string' &&
    'deck' in unwrapped
  )
}

export async function fetchDeckPlayerBootstrap(opts: {
  deckId: string
  model: DeckPlayerModel
  includeCards?: boolean
  restRoot?: string
  nonce?: string
}): Promise<DeckPlayerBootstrapV1> {
  const includeCards = opts.includeCards !== false

  const { restRoot, nonce } = resolvePerlingoRestContext({
    restRoot: opts.restRoot,
    nonce: opts.nonce,
  })

  const url = new URL(joinApi(restRoot, `/decks/${encodeURIComponent(opts.deckId)}/player`))
  url.searchParams.set('model', opts.model)
  url.searchParams.set('includeCards', includeCards ? 'true' : 'false')

  const response = await wpFetch(url.toString(), { method: 'GET' }, nonce)
  let json: unknown
  try {
    json = (await response.json()) as unknown
  } catch (e) {
    // If the transport failed or the server didn't return JSON, surface a useful error.
    if (!response.ok) throw e
    throw new Error('Deckplayer: failed to parse bootstrap JSON')
  }

  // Treat either transport errors OR structured ok:false payloads as an error.
  // Backend may return HTTP 200 even for incompatibility.
  const unwrapped = unwrapResponseJson(json)
  if (
    !response.ok ||
    (isRecord(unwrapped) && (unwrapped as Record<string, unknown>).ok === false)
  ) {
    throw json
  }

  if (!isBootstrap(json)) {
    throw new Error('Deckplayer: unexpected bootstrap response shape')
  }

  return unwrapResponseJson(json) as DeckPlayerBootstrapV1
}

const hasOkFalse = (json: unknown): boolean => {
  const unwrapped = unwrapResponseJson(json)
  return isRecord(unwrapped) && (unwrapped as Record<string, unknown>).ok === false
}

const parseJsonBestEffort = async (response: Response): Promise<unknown> => {
  try {
    return (await response.json()) as unknown
  } catch {
    return null
  }
}

export async function postDeckPlayerCardOpened(opts: {
  deckId: string
  model: DeckPlayerModel
  cardId: string
  restRoot?: string
  nonce?: string
}): Promise<{ ok: true } | unknown> {
  const { restRoot, nonce } = resolvePerlingoRestContext({
    restRoot: opts.restRoot,
    nonce: opts.nonce,
  })

  const url = joinApi(restRoot, `/decks/${encodeURIComponent(opts.deckId)}/player/card-opened`)
  const response = await wpFetch(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: opts.model, cardId: opts.cardId }),
    },
    nonce,
  )

  const json = await parseJsonBestEffort(response)
  if (!response.ok || hasOkFalse(json)) throw json
  return unwrapResponseJson(json) as unknown
}

export async function postDeckPlayerCheckboxState(opts: {
  deckId: string
  model: DeckPlayerModel
  cardId: string
  values: boolean[]
  addTimeMs?: number
  restRoot?: string
  nonce?: string
}): Promise<unknown> {
  const { restRoot, nonce } = resolvePerlingoRestContext({
    restRoot: opts.restRoot,
    nonce: opts.nonce,
  })

  const addTimeMs =
    typeof opts.addTimeMs === 'number' && Number.isFinite(opts.addTimeMs) && opts.addTimeMs > 0
      ? Math.floor(opts.addTimeMs)
      : 0

  const url = joinApi(restRoot, `/decks/${encodeURIComponent(opts.deckId)}/player/checkbox-state`)
  const response = await wpFetch(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: opts.model,
        cardId: opts.cardId,
        values: Array.isArray(opts.values) ? opts.values.map(Boolean) : [],
        addTimeMs,
      }),
    },
    nonce,
  )

  const json = await parseJsonBestEffort(response)
  if (!response.ok || hasOkFalse(json)) throw json
  return unwrapResponseJson(json)
}

export async function postDeckPlayerFlowProgress(opts: {
  deckId: string
  model: DeckPlayerModel
  phaseId: string
  checkboxStates: Record<string, boolean[]>
  restRoot?: string
  nonce?: string
}): Promise<unknown> {
  const { restRoot, nonce } = resolvePerlingoRestContext({
    restRoot: opts.restRoot,
    nonce: opts.nonce,
  })

  const url = joinApi(restRoot, `/decks/${encodeURIComponent(opts.deckId)}/player/flow-progress`)
  const response = await wpFetch(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: opts.model,
        phaseId: opts.phaseId,
        checkboxStates: opts.checkboxStates,
      }),
    },
    nonce,
  )

  const json = await parseJsonBestEffort(response)
  if (!response.ok || hasOkFalse(json)) throw json
  return unwrapResponseJson(json)
}

export async function postDeckPlayerReset(opts: {
  deckId: string
  model: DeckPlayerModel
  restRoot?: string
  nonce?: string
}): Promise<{ ok: true } | unknown> {
  const { restRoot, nonce } = resolvePerlingoRestContext({
    restRoot: opts.restRoot,
    nonce: opts.nonce,
  })

  const url = joinApi(restRoot, `/decks/${encodeURIComponent(opts.deckId)}/player/reset`)
  const response = await wpFetch(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model: opts.model }),
    },
    nonce,
  )

  const json = await parseJsonBestEffort(response)
  if (!response.ok || hasOkFalse(json)) throw json
  return unwrapResponseJson(json) as unknown
}

export async function postDeckPlayerRetryRequested(opts: {
  deckId: string
  model: DeckPlayerModel
  retryRequested: boolean
  restRoot?: string
  nonce?: string
}): Promise<{ ok: true } | unknown> {
  const { restRoot, nonce } = resolvePerlingoRestContext({
    restRoot: opts.restRoot,
    nonce: opts.nonce,
  })

  const url = joinApi(restRoot, `/decks/${encodeURIComponent(opts.deckId)}/player/retry-requested`)

  const response = await wpFetch(
    url,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: opts.model,
        retryRequested: Boolean(opts.retryRequested),
      }),
    },
    nonce,
  )

  const json = await parseJsonBestEffort(response)
  if (!response.ok || hasOkFalse(json)) throw json
  return unwrapResponseJson(json) as unknown
}
